/**
 * BugReportPanel -- Toolbox slide-out content for inline bug reporting.
 *
 * Auto-collects system diagnostics via bridge, lets the user describe the bug,
 * capture/paste/upload screenshots with annotation, attach log tails,
 * pick severity, record repro steps + expected/actual, and submit to
 * /api/reports/bug.
 *
 * The outer Toolbox panel already renders the "Report Bug" header, so this
 * component does not render its own title -- it starts straight from the form.
 *
 * Designed to live in the 240-800px toolbox panel. Uses ue.* Tailwind tokens.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Camera,
  Clipboard,
  Upload,
  Pencil,
  Loader2,
  Check,
  ChevronDown,
  ChevronRight,
  Send,
  X,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/utils/cn'
import { apiUrl } from '@/lib/api/apiUrl'
import { useBridge } from '@/core/bridge/useBridge'
import { unwrapBridgeResult } from '@/core/bridge/bridgeResult'
import { ScreenshotAnnotator } from './ScreenshotAnnotator'

// ============================================================================
// Types
// ============================================================================

interface InstalledPlugin {
  name: string
  version: string
  isGdb: boolean
}

interface SystemInfo {
  os: string
  ueVersion: string
  pluginVersion: string
  activeModule: string
  editionMode?: string
  projectName?: string
  cpuBrand?: string
  totalRamGb?: number
  gpuName?: string
  installedPlugins?: InstalledPlugin[]
}

interface AttachedFile {
  id: string
  name: string
  /** Always a `data:...` URL once the file is ready to submit. */
  dataUrl: string
  type: 'screenshot' | 'upload'
}

type Severity = 'low' | 'medium' | 'high' | 'critical'

const SEVERITY_OPTIONS: Array<{ value: Severity; label: string }> = [
  { value: 'low', label: 'Low -- minor annoyance' },
  { value: 'medium', label: 'Medium -- impairs workflow' },
  { value: 'high', label: 'High -- blocks progress' },
  { value: 'critical', label: 'Critical -- data loss or crash' },
]

// ============================================================================
// Helpers
// ============================================================================

/** Convert a Blob to a `data:` URL via FileReader. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('FileReader returned non-string result'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Downscale an image `data:` URL so attachments stay small enough to submit.
 * Full-resolution editor / Retina captures can be 10MB+ of base64 — far more
 * than a bug report needs — which is what tripped the upload size limit (413
 * on submit). Caps the longest edge and re-encodes as PNG (keeps UI text
 * crisp). Non-images (e.g. uploaded video) and already-small images pass
 * through unchanged. Best-effort: returns the original on any failure.
 */
async function downscaleImageDataUrl(dataUrl: string, maxEdge = 1920): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) return dataUrl
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('image decode failed'))
      img.src = dataUrl
    })
    const longest = Math.max(img.width, img.height)
    if (!longest || longest <= maxEdge) return dataUrl
    const scale = maxEdge / longest
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.width * scale)
    canvas.height = Math.round(img.height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) return dataUrl
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  } catch {
    return dataUrl
  }
}

/**
 * Resolve a screenshot reference from the bridge to a data URL.
 *
 * The C++ `CaptureEditorScreenshot` returns a URL path like
 * `/api/core/screenshots/bugreport_<ts>.png` served by the sidecar. We fetch
 * it and convert to a data URL so it can be submitted through the same
 * pipeline as pasted/uploaded images (and annotated later).
 *
 * If the bridge already returned a `data:` URL (e.g. future code paths),
 * it's passed through.
 */
/**
 * Map the bridge's GetSystemDiagnostics JSON onto our SystemInfo type. The
 * core fields are required; everything else is optional and best-effort —
 * we still want the report to submit if (e.g.) RAM detection fails on Linux.
 */
function parseSystemInfo(raw: unknown): SystemInfo {
  const r = (raw ?? {}) as Record<string, unknown>

  const pluginsRaw = Array.isArray(r.installedPlugins) ? r.installedPlugins : []
  const installedPlugins: InstalledPlugin[] = pluginsRaw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .map(p => ({
      name: String(p.name ?? ''),
      version: String(p.version ?? ''),
      isGdb: p.isGdb === true,
    }))
    .filter(p => p.name.length > 0)

  return {
    os: String(r.os ?? 'Unknown'),
    ueVersion: String(r.ueVersion ?? 'Unknown'),
    pluginVersion: String(r.pluginVersion ?? 'Unknown'),
    activeModule: String(r.activeModule ?? 'Unknown'),
    editionMode: typeof r.editionMode === 'string' ? r.editionMode : undefined,
    projectName: typeof r.projectName === 'string' ? r.projectName : undefined,
    cpuBrand: typeof r.cpuBrand === 'string' ? r.cpuBrand : undefined,
    totalRamGb: typeof r.totalRamGb === 'number' ? r.totalRamGb : undefined,
    gpuName: typeof r.gpuName === 'string' ? r.gpuName : undefined,
    installedPlugins: installedPlugins.length > 0 ? installedPlugins : undefined,
  }
}

async function resolveScreenshotToDataUrl(ref: string): Promise<string> {
  if (ref.startsWith('data:')) return ref
  const response = await fetch(ref, { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Failed to load screenshot (${response.status})`)
  }
  const blob = await response.blob()
  return blobToDataUrl(blob)
}

// ============================================================================
// Component
// ============================================================================

export interface BugReportPanelProps {
  /**
   * A screenshot the HOST captured before this panel became visible.
   *
   * The native capture is a `PrintWindow` of the whole top-level editor window,
   * so anything drawn over the editor is in the shot — including this reporter.
   * Captured on open, the "evidence" was a picture of the reporting tool sitting
   * on top of the bug (Craig, 2026-08-12). The fix is ordering, not cleverness:
   * the host grabs the frame while the bug is still on screen and hands us the
   * result here.
   */
  initialScreenshotRef?: string | null

  /**
   * Runs a capture with the host's UI out of the frame.
   *
   * Needed for the in-panel "Capture Editor" button, which fires while the
   * reporter is on screen by definition. The host hides itself, waits for the
   * WebView to actually repaint into the editor's composited frame, captures,
   * then restores. Defaults to running the capture as-is — the browser Toolbox
   * has nothing to hide.
   */
  prepareForCapture?: <T>(capture: () => Promise<T>) => Promise<T>

  /**
   * Called after a report is accepted. The host dismisses itself on this — a
   * form that clears but stays open reads as "did that send?", and leaves a
   * submitted report on screen to be attached to the next one.
   */
  onSubmitted?: () => void
}

export function BugReportPanel({
  initialScreenshotRef,
  prepareForCapture,
  onSubmitted,
}: BugReportPanelProps = {}) {
  const { isConnected, callFunction } = useBridge()

  // System info
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [systemInfoError, setSystemInfoError] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Form state
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<Severity>('medium')
  const [reproSteps, setReproSteps] = useState('')
  const [expected, setExpected] = useState('')
  const [actual, setActual] = useState('')

  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [logTail, setLogTail] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)

  // Annotation state
  const [annotatingFile, setAnnotatingFile] = useState<AttachedFile | null>(null)

  // Refs
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const idCounter = useRef(0)

  // --------------------------------------------------------------------------
  // System diagnostics
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isConnected) {
      setSystemInfoError(true)
      return
    }

    let cancelled = false

    const fetchDiagnostics = async () => {
      try {
        const result = unwrapBridgeResult(
          await callFunction('GDBCore.GetSystemDiagnostics'),
        )
        if (!cancelled && result && typeof result === 'object') {
          setSystemInfo(parseSystemInfo(result))
          setSystemInfoError(false)
        }
      } catch {
        if (!cancelled) setSystemInfoError(true)
      }
    }

    fetchDiagnostics()
    return () => {
      cancelled = true
    }
  }, [isConnected, callFunction])

  // --------------------------------------------------------------------------
  // File helpers
  // --------------------------------------------------------------------------

  const generateId = useCallback(() => {
    idCounter.current += 1
    return `file-${Date.now()}-${idCounter.current}`
  }, [])

  const addFile = useCallback(
    async (name: string, dataUrl: string, type: 'screenshot' | 'upload') => {
      const prepared = await downscaleImageDataUrl(dataUrl)
      setAttachedFiles(prev => [...prev, { id: generateId(), name, dataUrl: prepared, type }])
    },
    [generateId],
  )

  const removeFile = useCallback((id: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id))
  }, [])

  // Attach the host's pre-capture — the frame from before this panel covered
  // the editor. Guarded by a ref rather than the dependency list because
  // attaching twice under StrictMode's double mount would give every report a
  // duplicate screenshot.
  const initialShotConsumed = useRef(false)
  useEffect(() => {
    if (!initialScreenshotRef || initialShotConsumed.current) return
    initialShotConsumed.current = true

    void (async () => {
      try {
        const dataUrl = await resolveScreenshotToDataUrl(initialScreenshotRef)
        if (dataUrl.startsWith('data:')) {
          await addFile('Editor Screenshot', dataUrl, 'screenshot')
        }
      } catch (e) {
        // Non-fatal: the user can still capture manually or attach a file.
        console.warn('[BugReport] could not attach pre-capture screenshot', e)
      }
    })()
  }, [initialScreenshotRef, addFile])

  // --------------------------------------------------------------------------
  // Screenshot capture
  // --------------------------------------------------------------------------

  const handleCaptureEditor = useCallback(async () => {
    if (!isConnected) {
      toast.error('Bridge not connected -- cannot capture screenshot')
      return
    }

    setIsCapturing(true)
    try {
      // Through the host seam, so the reporter is off screen for the grab —
      // otherwise the user photographs this dialog instead of their bug.
      const runCapture = async () =>
        unwrapBridgeResult(await callFunction('GDBCore.CaptureEditorScreenshot'))
      const result = prepareForCapture ? await prepareForCapture(runCapture) : await runCapture()

      if (!result || typeof result !== 'string') {
        throw new Error('Capture returned no image')
      }

      const dataUrl = await resolveScreenshotToDataUrl(result)
      await addFile('Editor Screenshot', dataUrl, 'screenshot')
      toast.success('Screenshot captured')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Screenshot capture failed'
      toast.error(message)
    } finally {
      setIsCapturing(false)
    }
  }, [isConnected, callFunction, addFile, prepareForCapture])

  // --------------------------------------------------------------------------
  // Clipboard paste
  // --------------------------------------------------------------------------

  const handlePaste = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith('image/'))
        if (imageType) {
          const blob = await item.getType(imageType)
          const dataUrl = await blobToDataUrl(blob)
          await addFile('Pasted Image', dataUrl, 'screenshot')
          toast.success('Image pasted from clipboard')
          return
        }
      }
      toast.error('No image found in clipboard')
    } catch {
      toast.error('Failed to read clipboard -- try Ctrl+V in the description field')
    }
  }, [addFile])

  // --------------------------------------------------------------------------
  // File upload
  // --------------------------------------------------------------------------

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
          toast.error(`Unsupported file type: ${file.name}`)
          continue
        }

        try {
          const dataUrl = await blobToDataUrl(file)
          await addFile(file.name, dataUrl, 'upload')
        } catch {
          toast.error(`Failed to read ${file.name}`)
        }
      }

      // Reset input so the same file can be re-selected
      e.target.value = ''
    },
    [addFile],
  )

  // --------------------------------------------------------------------------
  // Annotation
  // --------------------------------------------------------------------------

  const handleAnnotationSave = useCallback(
    (annotatedDataUrl: string) => {
      if (!annotatingFile) return
      setAttachedFiles(prev =>
        prev.map(f => (f.id === annotatingFile.id ? { ...f, dataUrl: annotatedDataUrl } : f)),
      )
      setAnnotatingFile(null)
      toast.success('Annotation saved')
    },
    [annotatingFile],
  )

  const handleAnnotationCancel = useCallback(() => {
    setAnnotatingFile(null)
  }, [])

  // --------------------------------------------------------------------------
  // Log attachment
  // --------------------------------------------------------------------------

  const handleAttachLog = useCallback(async () => {
    if (!isConnected) {
      toast.error('Bridge not connected -- cannot fetch logs')
      return
    }

    try {
      const result = unwrapBridgeResult(await callFunction('GDBCore.GetLogTail'))
      if (result && typeof result === 'string') {
        setLogTail(result)
        toast.success('Log attached')
      } else {
        // An empty return is falsy, so this branch used to not exist and the
        // button simply did nothing at all — no log, no error, no clue. Say so.
        toast.error('Could not read the editor log')
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch log'
      toast.error(message)
    }
  }, [isConnected, callFunction])

  // --------------------------------------------------------------------------
  // Submit
  // --------------------------------------------------------------------------

  const canSubmit = description.trim().length > 0 && !isSubmitting

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return

    setIsSubmitting(true)

    try {
      // Auto-capture anything the user didn't already attach. The bell-driven
      // bug report needs to be useful even when the user just types a sentence
      // and hits Send — so on submit we top up systemInfo / log / screenshot
      // from the bridge if they're missing. Each step is best-effort: a failed
      // capture should never block the submission itself.

      let effectiveSystemInfo = systemInfo
      if (!effectiveSystemInfo && isConnected) {
        try {
          const result = unwrapBridgeResult(
            await callFunction('GDBCore.GetSystemDiagnostics'),
          )
          if (result && typeof result === 'object') {
            effectiveSystemInfo = parseSystemInfo(result)
            setSystemInfo(effectiveSystemInfo)
          }
        } catch (e) {
          console.warn('[BugReport] auto-fetch system diagnostics failed', e)
        }
      }

      let effectiveLogTail = logTail
      if (!effectiveLogTail && isConnected) {
        try {
          const result = unwrapBridgeResult(
            await callFunction('GDBCore.GetLogTail'),
          )
          if (typeof result === 'string' && result.length > 0) {
            effectiveLogTail = result
            setLogTail(result)
          }
        } catch (e) {
          console.warn('[BugReport] auto-fetch log tail failed', e)
        }
      }

      const existingScreenshots = attachedFiles
        .map(f => f.dataUrl)
        .filter(url => url.startsWith('data:'))

      const screenshots = [...existingScreenshots]
      if (screenshots.length === 0 && isConnected) {
        try {
          const runCapture = async () =>
            unwrapBridgeResult(await callFunction('GDBCore.CaptureEditorScreenshot'))
          const ref = prepareForCapture
            ? await prepareForCapture(runCapture)
            : await runCapture()
          if (typeof ref === 'string' && ref.length > 0) {
            const dataUrl = await resolveScreenshotToDataUrl(ref)
            if (dataUrl.startsWith('data:')) {
              screenshots.push(dataUrl)
              addFile('Auto-attached Editor Screenshot', dataUrl, 'screenshot')
            }
          }
        } catch (e) {
          console.warn('[BugReport] auto-capture screenshot failed', e)
        }
      }

      const body = {
        title: `[${severity.toUpperCase()}] ${description.trim().slice(0, 80)}`,
        description: description.trim(),
        severity,
        reproSteps: reproSteps.trim(),
        expected: expected.trim(),
        actual: actual.trim(),
        systemInfo: effectiveSystemInfo ?? {
          os: 'Unknown',
          ueVersion: 'Unknown',
          pluginVersion: 'Unknown',
          activeModule: 'Unknown',
        },
        logTail: effectiveLogTail ?? '',
        screenshots,
      }

      const response = await fetch(apiUrl('/api/reports/bug'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          (errorData as Record<string, string>).error || `Submit failed (${response.status})`,
        )
      }

      toast.success('Bug report submitted -- thank you!')

      // Clear form
      setDescription('')
      setSeverity('medium')
      setReproSteps('')
      setExpected('')
      setActual('')
      setAttachedFiles([])
      setLogTail(null)
      onSubmitted?.()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to submit bug report'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    canSubmit,
    description,
    severity,
    reproSteps,
    expected,
    actual,
    systemInfo,
    logTail,
    attachedFiles,
    isConnected,
    callFunction,
    addFile,
    prepareForCapture,
    onSubmitted,
  ])

  // --------------------------------------------------------------------------
  // Annotation mode -- full panel takeover
  // --------------------------------------------------------------------------

  if (annotatingFile) {
    return (
      <ScreenshotAnnotator
        imageUrl={annotatingFile.dataUrl}
        onSave={handleAnnotationSave}
        onCancel={handleAnnotationCancel}
      />
    )
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
      <div className="flex flex-col gap-3 p-3 flex-1 min-h-0">
        {/* Description */}
        <div className="flex flex-col shrink-0">
          <label
            htmlFor="bug-description"
            className="block text-ue-text-muted mb-1"
            style={{ fontSize: '10px' }}
          >
            DESCRIPTION <span className="text-ue-accent-red">*</span>
          </label>
          <textarea
            id="bug-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What happened? Keep it short -- more detail goes below."
            rows={3}
            className={cn(
              'w-full min-h-[64px] resize-y rounded px-2 py-1.5',
              'bg-ue-bg-darkest border border-ue-border-medium',
              'text-ue-text-primary placeholder:text-ue-text-muted',
              'focus:outline-none focus:border-ue-accent-primary/50',
            )}
            style={{ fontSize: '11px' }}
          />
        </div>

        {/* Severity */}
        <div className="shrink-0">
          <label
            htmlFor="bug-severity"
            className="block text-ue-text-muted mb-1"
            style={{ fontSize: '10px' }}
          >
            SEVERITY
          </label>
          <select
            id="bug-severity"
            value={severity}
            onChange={e => setSeverity(e.target.value as Severity)}
            className={cn(
              'w-full rounded px-2 py-1.5',
              'bg-ue-bg-darkest border border-ue-border-medium',
              'text-ue-text-primary',
              'focus:outline-none focus:border-ue-accent-primary/50',
            )}
            style={{ fontSize: '11px' }}
          >
            {SEVERITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Steps to reproduce */}
        <div className="shrink-0">
          <label
            htmlFor="bug-repro"
            className="block text-ue-text-muted mb-1"
            style={{ fontSize: '10px' }}
          >
            STEPS TO REPRODUCE
          </label>
          <textarea
            id="bug-repro"
            value={reproSteps}
            onChange={e => setReproSteps(e.target.value)}
            placeholder="1. Open the document\n2. Click X\n3. Observe the crash"
            rows={3}
            className={cn(
              'w-full min-h-[56px] resize-y rounded px-2 py-1.5',
              'bg-ue-bg-darkest border border-ue-border-medium',
              'text-ue-text-primary placeholder:text-ue-text-muted',
              'focus:outline-none focus:border-ue-accent-primary/50',
            )}
            style={{ fontSize: '11px' }}
          />
        </div>

        {/* Expected vs Actual */}
        <div className="grid grid-cols-1 gap-3 shrink-0">
          <div>
            <label
              htmlFor="bug-expected"
              className="block text-ue-text-muted mb-1"
              style={{ fontSize: '10px' }}
            >
              EXPECTED RESULT
            </label>
            <textarea
              id="bug-expected"
              value={expected}
              onChange={e => setExpected(e.target.value)}
              placeholder="What did you think would happen?"
              rows={2}
              className={cn(
                'w-full min-h-[44px] resize-y rounded px-2 py-1.5',
                'bg-ue-bg-darkest border border-ue-border-medium',
                'text-ue-text-primary placeholder:text-ue-text-muted',
                'focus:outline-none focus:border-ue-accent-primary/50',
              )}
              style={{ fontSize: '11px' }}
            />
          </div>
          <div>
            <label
              htmlFor="bug-actual"
              className="block text-ue-text-muted mb-1"
              style={{ fontSize: '10px' }}
            >
              ACTUAL RESULT
            </label>
            <textarea
              id="bug-actual"
              value={actual}
              onChange={e => setActual(e.target.value)}
              placeholder="What happened instead?"
              rows={2}
              className={cn(
                'w-full min-h-[44px] resize-y rounded px-2 py-1.5',
                'bg-ue-bg-darkest border border-ue-border-medium',
                'text-ue-text-primary placeholder:text-ue-text-muted',
                'focus:outline-none focus:border-ue-accent-primary/50',
              )}
              style={{ fontSize: '11px' }}
            />
          </div>
        </div>

        {/* Screenshots section */}
        <div className="shrink-0">
          <label className="block text-ue-text-muted mb-1.5" style={{ fontSize: '10px' }}>
            SCREENSHOTS &amp; FILES
          </label>

          <div className="flex gap-1.5 mb-2 flex-wrap">
            <button
              onClick={handleCaptureEditor}
              disabled={!isConnected || isCapturing}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded border transition-colors',
                'border-ue-border-medium',
                isConnected && !isCapturing
                  ? 'text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary'
                  : 'text-ue-text-muted cursor-not-allowed opacity-50',
              )}
              style={{ fontSize: '10px' }}
              title={isConnected ? 'Capture editor screenshot' : 'Bridge disconnected'}
            >
              {isCapturing ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Camera size={12} />
              )}
              Capture
            </button>

            <button
              onClick={handlePaste}
              className="flex items-center gap-1 px-2 py-1 rounded border border-ue-border-medium text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary transition-colors"
              style={{ fontSize: '10px' }}
              title="Paste image from clipboard"
            >
              <Clipboard size={12} />
              Paste
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1 rounded border border-ue-border-medium text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary transition-colors"
              style={{ fontSize: '10px' }}
              title="Upload image or video"
            >
              <Upload size={12} />
              Upload
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {/* Attached files list */}
          {attachedFiles.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {attachedFiles.map(file => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 p-1.5 rounded bg-ue-bg-darkest border border-ue-border-medium"
                >
                  {/* Thumbnail */}
                  {file.dataUrl.startsWith('data:image') ? (
                    <button
                      onClick={() => setAnnotatingFile(file)}
                      className="relative shrink-0 w-10 h-10 rounded overflow-hidden border border-ue-border-medium hover:border-ue-accent-primary/50 transition-colors group"
                      title="Click to annotate"
                    >
                      <img
                        src={file.dataUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Pencil size={10} className="text-white" />
                      </div>
                    </button>
                  ) : (
                    <div className="shrink-0 w-10 h-10 rounded bg-ue-bg-darker border border-ue-border-medium flex items-center justify-center">
                      <FileText size={14} className="text-ue-text-muted" />
                    </div>
                  )}

                  {/* File name */}
                  <span
                    className="flex-1 text-ue-text-secondary truncate"
                    style={{ fontSize: '10px' }}
                  >
                    {file.name}
                  </span>

                  {/* Remove button */}
                  <button
                    onClick={() => removeFile(file.id)}
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-ue-text-muted hover:text-ue-accent-red hover:bg-white/5 transition-colors"
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Log attachment */}
        <div className="shrink-0">
          <label className="block text-ue-text-muted mb-1.5" style={{ fontSize: '10px' }}>
            LOG FILE
          </label>

          {logTail ? (
            <div className="flex items-center gap-2 p-1.5 rounded bg-ue-bg-darkest border border-ue-border-medium">
              <Check size={12} className="text-green-400 shrink-0" />
              <span className="flex-1 text-green-400" style={{ fontSize: '10px' }}>
                Log attached
              </span>
              <button
                onClick={() => setLogTail(null)}
                className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-ue-text-muted hover:text-ue-accent-red hover:bg-white/5 transition-colors"
                title="Remove log"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAttachLog}
              disabled={!isConnected}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded border transition-colors',
                'border-ue-border-medium',
                isConnected
                  ? 'text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary'
                  : 'text-ue-text-muted cursor-not-allowed opacity-50',
              )}
              style={{ fontSize: '10px' }}
              title={isConnected ? 'Attach last 500 lines of log' : 'Bridge disconnected'}
            >
              <FileText size={12} />
              Attach Log File
            </button>
          )}
        </div>

        {/* Details being attached -- collapsible */}
        <div className="shrink-0">
          <button
            type="button"
            onClick={() => setDetailsOpen(prev => !prev)}
            aria-expanded={detailsOpen}
            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-ue-bg-darkest border border-ue-border-medium text-ue-text-secondary hover:text-ue-text-primary hover:bg-white/5 transition-colors"
            style={{ fontSize: '10px' }}
          >
            <span className="flex items-center gap-1.5">
              {detailsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Details being attached
            </span>
            <span className="text-ue-text-muted">
              {systemInfo ? 'Auto-collected' : systemInfoError ? 'Unavailable' : 'Loading...'}
            </span>
          </button>

          {detailsOpen && (
            <div className="mt-1.5 p-2 rounded bg-ue-bg-darkest border border-ue-border-medium">
              {systemInfo ? (
                <dl
                  className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-ue-text-secondary"
                  style={{ fontSize: '10px' }}
                >
                  <dt className="text-ue-text-muted">OS</dt>
                  <dd className="truncate">{systemInfo.os}</dd>
                  <dt className="text-ue-text-muted">UE Version</dt>
                  <dd className="truncate">{systemInfo.ueVersion}</dd>
                  <dt className="text-ue-text-muted">Plugin Version</dt>
                  <dd className="truncate">{systemInfo.pluginVersion}</dd>
                  <dt className="text-ue-text-muted">Active Module</dt>
                  <dd className="truncate">{systemInfo.activeModule}</dd>
                  {systemInfo.editionMode && (
                    <>
                      <dt className="text-ue-text-muted">Edition</dt>
                      <dd className="truncate">{systemInfo.editionMode}</dd>
                    </>
                  )}
                  {systemInfo.projectName && (
                    <>
                      <dt className="text-ue-text-muted">Project</dt>
                      <dd className="truncate">{systemInfo.projectName}</dd>
                    </>
                  )}
                  {systemInfo.cpuBrand && (
                    <>
                      <dt className="text-ue-text-muted">CPU</dt>
                      <dd className="truncate">{systemInfo.cpuBrand}</dd>
                    </>
                  )}
                  {typeof systemInfo.totalRamGb === 'number' && (
                    <>
                      <dt className="text-ue-text-muted">RAM</dt>
                      <dd className="truncate">{systemInfo.totalRamGb.toFixed(1)} GB</dd>
                    </>
                  )}
                  {systemInfo.gpuName && (
                    <>
                      <dt className="text-ue-text-muted">GPU</dt>
                      <dd className="truncate">{systemInfo.gpuName}</dd>
                    </>
                  )}
                  {systemInfo.installedPlugins && systemInfo.installedPlugins.length > 0 && (
                    <>
                      <dt className="text-ue-text-muted">Plugins</dt>
                      <dd className="truncate">
                        {systemInfo.installedPlugins.filter(p => p.isGdb).length} GDB,{' '}
                        {systemInfo.installedPlugins.filter(p => !p.isGdb).length} other
                      </dd>
                    </>
                  )}
                  <dt className="text-ue-text-muted">Log File</dt>
                  <dd className="truncate">{logTail ? 'Attached' : 'Not attached'}</dd>
                  <dt className="text-ue-text-muted">Screenshots</dt>
                  <dd className="truncate">{attachedFiles.length}</dd>
                </dl>
              ) : (
                <p className="text-ue-text-muted italic" style={{ fontSize: '10px' }}>
                  {systemInfoError
                    ? 'System info unavailable -- the bridge may be disconnected.'
                    : 'Loading system info...'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Submit button -- sticky at bottom */}
      <div className="shrink-0 p-3 border-t border-ue-border-medium">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded font-medium transition-colors',
            canSubmit
              ? 'bg-ue-accent-primary text-white hover:bg-ue-accent-primary/90'
              : 'bg-ue-bg-darkest text-ue-text-muted cursor-not-allowed',
          )}
          style={{ fontSize: '11px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={14} />
              Send Report
            </>
          )}
        </button>
      </div>
    </div>
  )
}
